/** Mirror backend hair_recommendation_inputs (Gemma detections + magnified macro). */

import { normalizeHairDetectionLabel } from './hairAnalysisLabels';

const MIN_CONFIDENCE_PCT = 35;

function confidencePercent(det) {
  let conf = Number(det?.confidence);
  if (!Number.isFinite(conf)) conf = 0;
  if (conf > 0 && conf <= 1) conf *= 100;
  return Math.max(0, Math.min(100, conf));
}

function iterHairDetections(hairResults) {
  if (!hairResults || typeof hairResults !== 'object') return [];
  const found = [];
  const positionResults = hairResults.position_results || hairResults.positionResults || {};
  Object.values(positionResults).forEach((pos) => {
    if (!pos || typeof pos !== 'object') return;
    const res = pos.results && typeof pos.results === 'object' ? pos.results : {};
    ['raw_detections', 'detections', 'all_detections'].forEach((key) => {
      const items = pos[key] || res[key] || res.detections;
      if (Array.isArray(items)) found.push(...items);
    });
  });
  ['detections', 'raw_detections'].forEach((key) => {
    const items = hairResults[key] || hairResults.results?.[key];
    if (Array.isArray(items)) found.push(...items);
  });
  return found;
}

function magnifiedMacroConcerns(magnified) {
  if (!magnified || typeof magnified !== 'object') return [];
  const metrics = magnified.metrics || {};
  const out = [];

  const densityPx = Number(metrics.hair_density?.value_px);
  if (Number.isFinite(densityPx) && densityPx > 0) {
    if (densityPx <= 0.12) out.push(['Thinning Stage 2', 20]);
    else if (densityPx <= 0.25) out.push(['Thinning Stage 1', 48]);
  }

  const exposure = Number(metrics.scalp_exposure_ratio?.value);
  if (Number.isFinite(exposure)) {
    if (exposure > 0.65) out.push(['Thinning Stage 3', 22]);
    else if (exposure > 0.45) out.push(['Thinning Stage 2', 48]);
  }

  const gapPx = Number(metrics.hair_gap?.average_px);
  if (Number.isFinite(gapPx) && gapPx > 45) out.push(['Thinning Stage 2', 25]);

  const folPx = Number(metrics.follicle_spacing?.mean_px);
  if (Number.isFinite(folPx) && folPx > 45) out.push(['Thinning Stage 2', 30]);

  const thickLevel = String(metrics.hair_thickness?.level || '').toLowerCase();
  if (thickLevel === 'very fine' || thickLevel === 'fine') {
    out.push(['Rough/Straw Hair', 35]);
  }

  return out;
}

function mergeConcernScore(scores, label, concernScore) {
  const prev = scores[label];
  if (prev === undefined) scores[label] = concernScore;
  else scores[label] = Math.min(prev, concernScore);
}

/** @returns {{ condition_scores: Record<string, number>, detected_conditions: string[] }} */
export function buildHairRecommendationInputs(hairResults) {
  const scores = {};
  const maxConf = {};

  iterHairDetections(hairResults).forEach((det) => {
    const raw = det?.label || det?.pure_label || det?.class_name || '';
    const lower = String(raw).toLowerCase();
    if (!raw || lower === 'healthy scalp' || lower === 'normal scalp') return;
    const mapped = normalizeHairDetectionLabel(raw);
    if (!mapped) return;
    const conf = confidencePercent(det);
    if (conf < MIN_CONFIDENCE_PCT) return;
    maxConf[mapped] = Math.max(maxConf[mapped] || 0, conf);
  });

  Object.entries(maxConf).forEach(([label, conf]) => {
    mergeConcernScore(scores, label, Math.round(100 - conf));
  });

  const magnified = hairResults?.magnified_analysis || hairResults?.magnifiedAnalysis;
  magnifiedMacroConcerns(magnified).forEach(([label, concernScore]) => {
    mergeConcernScore(scores, label, concernScore);
  });

  const detected_conditions = Object.keys(scores).sort();
  return { condition_scores: scores, detected_conditions };
}
